/**
 * Scroll infinito nativo per collezione e ricerca (sostituisce l'app Scrollify).
 * Stessa logica di Paskal e Brancaccio:
 * - carica la pagina successiva quando ci si avvicina al fondo;
 * - aggiorna page=N nell'URL in base al primo prodotto visibile (sale scendendo,
 *   scende risalendo), cosi' tornando indietro da un prodotto il server rende
 *   direttamente la pagina giusta;
 * - si reinizializza quando i filtri AJAX del tema sostituiscono la griglia.
 * In piu', al ritorno riporta la card cliccata alla stessa altezza dello schermo.
 */
(() => {
  const path = window.location.pathname;
  if (!path.includes('/collections/') && !path.includes('/search')) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (window.__LAR_INFINITE__) return;
  window.__LAR_INFINITE__ = true;

  const GRID = '#product-grid';
  const RETURN_KEY = 'larCollectionReturn';
  const state = { key: null, nextUrl: null, loading: false };

  const getGrid = () => document.querySelector(GRID);
  const getPagination = () => document.querySelector('custom-pagination .js-pagination');
  const getSentinel = () => {
    const pagination = getPagination();
    return pagination ? pagination.closest('nav') || pagination : null;
  };

  function urlPage() {
    try {
      return parseInt(new URL(window.location.href).searchParams.get('page') || '1', 10) || 1;
    } catch (err) {
      return 1;
    }
  }

  function filterKey() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      return `${url.pathname}?${url.searchParams.toString()}`;
    } catch (err) {
      return window.location.pathname;
    }
  }

  function readNextUrl(root) {
    const link = root.querySelector('.js-pagination .pagination__arrow--next[href]');
    if (!link) return null;
    try {
      return new URL(link.getAttribute('href'), window.location.href).toString();
    } catch (err) {
      return null;
    }
  }

  function initState() {
    state.key = filterKey();
    state.loading = false;
    state.nextUrl = readNextUrl(document);

    const grid = getGrid();
    if (!grid) return;
    const page = urlPage();
    [...grid.children].forEach((el) => {
      if (!el.hasAttribute('data-lar-page')) el.setAttribute('data-lar-page', page);
    });
  }

  function setUrlPage(page) {
    try {
      const url = new URL(window.location.href);
      if (page > 1) {
        url.searchParams.set('page', page);
      } else {
        url.searchParams.delete('page');
      }
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (err) {} // eslint-disable-line
  }

  function stripAnimations(el) {
    el.removeAttribute('data-cc-animate');
    el.removeAttribute('data-cc-animate-delay');
    el.querySelectorAll('[data-cc-animate]').forEach((animated) => {
      animated.removeAttribute('data-cc-animate');
      animated.removeAttribute('data-cc-animate-delay');
    });
  }

  async function loadNext() {
    if (state.loading || !state.nextUrl) return;
    state.loading = true;

    const key = state.key;
    const url = state.nextUrl;
    const sentinel = getSentinel();
    const loader = document.createElement('div');
    loader.className = 'lar-infinite-loader';
    if (sentinel) sentinel.appendChild(loader);

    let page = 1;
    try {
      page = parseInt(new URL(url).searchParams.get('page') || '1', 10);
    } catch (err) {} // eslint-disable-line

    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Nel frattempo sono cambiati i filtri: questa pagina non serve piu'
      if (filterKey() !== key) return;

      const newGrid = doc.querySelector(GRID);
      const grid = getGrid();
      if (newGrid && grid) {
        [...newGrid.children].forEach((el) => {
          stripAnimations(el);
          el.setAttribute('data-lar-page', page);
          grid.appendChild(document.importNode(el, true));
        });
        if (typeof window.initLazyImages === 'function') window.initLazyImages();
      }
      state.nextUrl = readNextUrl(doc);
    } catch (err) {
      console.log('Unable to fetch next page of results', err); // eslint-disable-line
    } finally {
      loader.remove();
      if (filterKey() === key) state.loading = false;
    }
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (filterKey() !== state.key) initState();
        loadNext();
      });
    },
    { rootMargin: '600px 0px' }
  );

  function bindObserver() {
    const sentinel = getSentinel();
    if (sentinel) io.observe(sentinel);
  }

  let scrollTick = null;
  function updateUrlByScroll() {
    if (scrollTick) return;
    scrollTick = setTimeout(() => {
      scrollTick = null;
      if (filterKey() !== state.key) return;
      const current = [...document.querySelectorAll(`${GRID} > [data-lar-page]`)].find(
        (el) => el.getBoundingClientRect().bottom > 160
      );
      if (!current) return;
      const page = parseInt(current.getAttribute('data-lar-page'), 10) || 1;
      if (page !== urlPage()) setUrlPage(page);
    }, 250);
  }

  // Salva la card cliccata e la sua distanza dal bordo alto della finestra
  function saveReturn(evt) {
    const link = evt.target.closest(`${GRID} a[href*="/products/"]`);
    if (!link) return;
    const card = link.closest(`${GRID} > *`) || link;
    try {
      sessionStorage.setItem(
        RETURN_KEY,
        JSON.stringify({
          key: filterKey(),
          href: link.getAttribute('href'),
          top: Math.round(card.getBoundingClientRect().top)
        })
      );
    } catch (err) {} // eslint-disable-line
  }

  // Al ritorno il server rende gia' la pagina giusta: resta da rimettere la
  // card dov'era, perche' il browser ripristina lo scorrimento di una pagina
  // che prima era molto piu' lunga
  function restoreReturn() {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(RETURN_KEY));
    } catch (err) {} // eslint-disable-line
    if (!saved) return;

    const nav = performance.getEntriesByType('navigation')[0];
    if (!nav || nav.type !== 'back_forward' || saved.key !== filterKey()) {
      try {
        sessionStorage.removeItem(RETURN_KEY);
      } catch (err) {} // eslint-disable-line
      return;
    }

    const grid = getGrid();
    const findCard = () => {
      const link = [...grid.querySelectorAll('a[href*="/products/"]')].find(
        (a) => a.getAttribute('href') === saved.href
      );
      return link ? link.closest(`${GRID} > *`) || link : null;
    };

    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

    const userEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    let observer;
    let timers = [];
    const stop = () => {
      if (observer) observer.disconnect();
      timers.forEach(clearTimeout);
      timers = [];
      userEvents.forEach((type) => window.removeEventListener(type, stop));
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto';
    };

    const place = () => {
      const card = findCard();
      if (!card) return false;
      const target = card.getBoundingClientRect().top + window.scrollY - saved.top;
      window.scrollTo({ top: Math.max(0, target), behavior: 'instant' });
      return true;
    };

    const settle = () => {
      if (!place()) return;
      if (observer) observer.disconnect();
      // Le immagini arrivano dopo e possono spostare le righe
      timers.push(setTimeout(place, 300), setTimeout(place, 1000), setTimeout(stop, 1100));
    };

    // Se il visitatore tocca la pagina, smettiamo di spostarla
    userEvents.forEach((type) => window.addEventListener(type, stop, { passive: true }));

    if (findCard()) {
      settle();
    } else {
      // La card era gia' della pagina dopo: arriva col prossimo caricamento
      observer = new MutationObserver(settle);
      observer.observe(grid, { childList: true });
      timers.push(setTimeout(stop, 8000));
    }
  }

  function start() {
    if (!getGrid()) return;
    document.documentElement.classList.add('lar-infinite');
    initState();
    bindObserver();
    restoreReturn();

    // I filtri AJAX del tema sostituiscono griglia e paginazione
    const results = document.getElementById('filter-results');
    if (results) {
      new MutationObserver(() => {
        if (!getGrid() || getGrid().querySelector(':scope > [data-lar-page]')) return;
        initState();
        bindObserver();
      }).observe(results, { childList: true });
    }

    document.addEventListener('scroll', updateUrlByScroll, { passive: true });
    document.addEventListener('click', saveReturn, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
